import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronsLeftRight, Images } from "lucide-react";
import { GALLERY_ITEMS } from "../data/content";

const BEFORE_FILTER = "grayscale(0.8) brightness(0.55) sepia(0.35) contrast(0.85)";

const CompareSlider = ({ item }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid={`ba-slider-${item.id}`}
      className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 select-none touch-none cursor-ew-resize"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => {
        if (e.buttons === 1) update(e.clientX);
      }}
    >
      <img
        src={item.image}
        alt={`${item.label} depois da limpeza`}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <img
        src={item.image}
        alt={`${item.label} antes da limpeza`}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)`, filter: BEFORE_FILTER }}
      />

      <span className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-300">
        Antes
      </span>
      <span className="absolute top-3 right-3 rounded-full bg-sky-500/80 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
        Depois
      </span>

      <div
        data-testid={`ba-handle-${item.id}`}
        className="absolute inset-y-0"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute inset-y-0 -left-px w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,168,232,0.8)]" />
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#0A1128] shadow-xl shadow-black/40">
          <ChevronsLeftRight className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  return (
    <section id="galeria" data-testid="gallery-section" className="py-20 sm:py-28 bg-[#0B1430]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-sky-300">
            <Images className="h-3.5 w-3.5" />
            Galeria Antes / Depois
          </span>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Arraste e veja o efeito novo
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            Deslize o cursor sobre cada imagem para comparar o antes e o depois
            de uma limpeza profissional.
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {GALLERY_ITEMS.map((item, i) => (
            <motion.figure
              key={item.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
            >
              <CompareSlider item={item} />
              <figcaption
                data-testid={`ba-label-${item.id}`}
                className="mt-3 text-center font-display text-sm font-bold text-slate-200"
              >
                {item.label}
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <p data-testid="gallery-note" className="mt-8 text-center text-xs text-slate-500">
          Fotografias ilustrativas do tipo de resultado. Envie fotos do que quer
          limpar pelo WhatsApp e mostramos o que podemos fazer.
        </p>
      </div>
    </section>
  );
};
